import React from "react";
import { motion } from "framer-motion";
import { Check, Calendar, Sparkles } from "lucide-react";
import { Button } from "./shared/Button";
import { useI18n } from "../../i18n/I18nProvider"; // adjust path if needed

interface SuccessStepProps {
  planName?: string;
  trialStatus?: string;
  trialEndDate?: string;
}

export function SuccessStep({
  planName = "Active Matching",
  trialStatus = "7-day trial",
  trialEndDate = "January 15, 2025",
}: SuccessStepProps) {
  const { t } = useI18n();

  return (
    <div className="flex flex-col items-center justify-center min-h-[70vh] text-center space-y-16 relative">
      {/* Success Icon */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
        className="relative"
      >
        <div className="w-32 h-32 rounded-full animated-gradient-circle flex items-center justify-center organic-shadow">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <Check className="w-16 h-16 text-white stroke-[1.5] relative z-10" />
          </motion.div>
        </div>
      </motion.div>

      {/* Title and Subtitle */}
      <div className="space-y-6 max-w-2xl mx-auto">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
          className="text-5xl sm:text-6xl font-normal text-[#2D2A35] tracking-tight"
        >
          {t("success.title")}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
          className="text-xl sm:text-2xl text-[#57534E] leading-relaxed font-light"
        >
          {t("success.body")}
        </motion.p>
      </div>

      {/* Summary Card */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-3xl p-10 border border-[#E8E4EE] shadow-lg space-y-8">
          {/* Plan Name */}
          <div className="flex items-center justify-between pb-6 border-b border-[#E8E4EE]">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#F8F8FA] flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-[#9B87C7]" />
              </div>
              <div className="text-left">
                <p className="text-sm text-[#9B95A3] font-medium uppercase tracking-wider">
                  {t("success.card.planLabel")}
                </p>
                <p className="text-xl font-medium text-[#2D2A35] mt-1">{planName}</p>
              </div>
            </div>
          </div>

          {/* Trial Status */}
          <div className="flex items-center justify-between pb-6 border-b border-[#E8E4EE]">
            <div className="text-left">
              <p className="text-sm text-[#9B95A3] font-medium uppercase tracking-wider">
                {t("success.card.statusLabel")}
              </p>
              <p className="text-lg text-[#2D2A35] mt-2 font-light">{trialStatus}</p>
            </div>
          </div>

          {/* Trial End Date */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#F8F8FA] flex items-center justify-center">
                <Calendar className="w-6 h-6 text-[#9B87C7]" />
              </div>
              <div className="text-left">
                <p className="text-sm text-[#9B95A3] font-medium uppercase tracking-wider">
                  {t("success.card.trialEndsLabel")}
                </p>
                <p className="text-lg text-[#2D2A35] mt-2 font-light">{trialEndDate}</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* CTA Button */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.0, duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
        className="w-full max-w-xs pt-8"
      >
        <Button
          onClick={() => (window.location.href = "/dashboard")}
          fullWidth
          className="text-lg py-6"
        >
          {t("success.cta")}
        </Button>
      </motion.div>

      {/* Footer Note */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
        className="pt-8"
      >
        <p className="text-sm text-[#B5A8C3] font-light tracking-wide">
          {t("success.footer")}
        </p>
      </motion.div>
    </div>
  );
}